import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";

function GalleryAlbum() {
  const { albumId } = useParams();
  const [album, setAlbum] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    fetchData();
  }, [albumId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [albumRes, settingsRes] = await Promise.all([
        axiosInstance.get(`/api/gallery/albums/${albumId}`),
        axiosInstance.get("/api/settings"),
      ]);
      setAlbum(albumRes.data);
      setSettings(settingsRes.data);
    } catch (err) {
      console.error("Failed to load album", err);
      setAlbum(null);
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = settings?.theme_primary_color || "#0d6efd";
  const photos = album?.photos || [];

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const showPrev = () =>
    setLightboxIndex((i) => (i === 0 ? photos.length - 1 : i - 1));
  const showNext = () =>
    setLightboxIndex((i) => (i === photos.length - 1 ? 0 : i + 1));

  // Keyboard navigation for the lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxIndex, photos.length]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="container py-5 text-center">
        <p className="text-muted">Album not found.</p>
        <Link to="/gallery" className="btn btn-outline-primary">
          Back to Gallery
        </Link>
      </div>
    );
  }

  return (
    <div>
      <section
        className="py-4 text-white"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="container">
          <Link
            to="/gallery"
            className="text-white-50 text-decoration-none small"
          >
            <i className="bi bi-arrow-left me-1"></i> Back to Gallery
          </Link>
          <h1 className="fw-bold mb-0 mt-2">{album.title}</h1>
          {album.description && (
            <p className="mb-0 opacity-75">{album.description}</p>
          )}
        </div>
      </section>

      <div className="container py-5">
        {photos.length === 0 ? (
          <p className="text-muted text-center py-5">
            No photos in this album yet.
          </p>
        ) : (
          <div className="row g-3">
            {photos.map((photo, index) => (
              <div className="col-6 col-sm-4 col-lg-3" key={photo.id}>
                <div
                  role="button"
                  onClick={() => openLightbox(index)}
                  style={{
                    cursor: "pointer",
                    overflow: "hidden",
                    borderRadius: "8px",
                  }}
                >
                  <img
                    src={`${process.env.REACT_APP_API_URL}/${photo.image_path}`}
                    alt={photo.caption || album.title}
                    className="w-100"
                    style={{ height: "160px", objectFit: "cover" }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: "rgba(0,0,0,0.9)", zIndex: 2000 }}
          onClick={closeLightbox}
        >
          <button
            className="btn btn-light position-absolute"
            style={{ top: "20px", right: "20px", zIndex: 2001 }}
            onClick={closeLightbox}
          >
            <i className="bi bi-x-lg"></i>
          </button>

          {photos.length > 1 && (
            <button
              className="btn btn-light position-absolute"
              style={{ left: "20px", zIndex: 2001 }}
              onClick={(e) => {
                e.stopPropagation();
                showPrev();
              }}
            >
              <i className="bi bi-chevron-left"></i>
            </button>
          )}

          <div
            className="text-center"
            style={{ maxWidth: "90%", maxHeight: "85vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`${process.env.REACT_APP_API_URL}/${photos[lightboxIndex].image_path}`}
              alt={photos[lightboxIndex].caption || album.title}
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
              }}
            />
            {photos[lightboxIndex].caption && (
              <p className="text-white mt-3 mb-0">
                {photos[lightboxIndex].caption}
              </p>
            )}
            <p className="text-white-50 small mt-1">
              {lightboxIndex + 1} / {photos.length}
            </p>
          </div>

          {photos.length > 1 && (
            <button
              className="btn btn-light position-absolute"
              style={{ right: "20px", zIndex: 2001 }}
              onClick={(e) => {
                e.stopPropagation();
                showNext();
              }}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default GalleryAlbum;
